import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  ArrowLeft,
  Trash2,
  Eye,
  RefreshCw,
  Search,
  Filter,
  LogOut,
  FileText,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Admin() {
  const { authAxios, logout, user } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const response = await authAxios.get("/contact");
      setEnquiries(response.data.reverse());
    } catch (error) {
      toast.error("Failed to load enquiries");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
    document.title = "Admin Panel | Four Bees Consultancy";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, newStatus) => {
    try {
      await authAxios.put(`/contact/${id}`, { status: newStatus });
      setEnquiries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, status: newStatus } : e))
      );
      if (selectedEnquiry?.id === id) {
        setSelectedEnquiry((prev) => ({ ...prev, status: newStatus }));
      }
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteEnquiry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this enquiry?")) return;
    try {
      await authAxios.delete(`/contact/${id}`);
      setEnquiries((prev) => prev.filter((e) => e.id !== id));
      if (selectedEnquiry?.id === id) setSelectedEnquiry(null);
      toast.success("Enquiry deleted");
    } catch (error) {
      toast.error("Failed to delete enquiry");
    }
  };

  const filteredEnquiries = enquiries.filter((e) => {
    const matchesSearch =
      e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.message?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || e.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getStatusColor = (status) => {
    switch (status) {
      case "new":
        return "bg-gold text-navy";
      case "contacted":
        return "bg-navy text-cream";
      case "completed":
        return "bg-forest-green text-white";
      default:
        return "bg-warm-gray text-charcoal";
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-panel">
      <header className="bg-navy text-white py-4 sticky top-0 z-50">
        <div className="section-container flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <a
              href="/"
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              data-testid="admin-back-link"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Back to Website</span>
            </a>
            <div className="h-6 w-px bg-white/20" />
            <h1 className="font-heading text-xl font-semibold truncate">Admin Panel</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-sm text-white/70" data-testid="admin-user-email">
              {user?.email}
            </span>
            <span className="text-sm text-white/70 whitespace-nowrap">
              {enquiries.length} Total
            </span>
            <Link
              to="/admin/content"
              className="inline-flex items-center gap-2 px-3 py-2 bg-gold text-navy hover:bg-gold-dark rounded-sm transition-colors text-sm font-semibold"
              data-testid="admin-cms-link"
            >
              <FileText size={16} />
              <span className="hidden sm:inline">Edit Site Content</span>
            </Link>
            <button
              onClick={fetchEnquiries}
              className="p-2 hover:bg-white/10 rounded-sm transition-colors"
              aria-label="Refresh"
              data-testid="refresh-enquiries-btn"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-gold hover:text-charcoal rounded-sm transition-colors text-sm"
              data-testid="admin-logout-button"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="section-container py-8">
        <div className="bg-white border border-border-gray rounded-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                size={18}
              />
              <input
                type="text"
                placeholder="Search by name, email, company, or message..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border-gray rounded-sm focus:outline-none focus:border-gold"
                data-testid="admin-search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter size={18} className="text-muted-foreground" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-border-gray rounded-sm focus:outline-none focus:border-gold bg-white"
                data-testid="admin-status-filter"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white border border-border-gray rounded-sm overflow-hidden">
              <div className="bg-navy text-white px-4 py-3">
                <h2 className="font-heading font-semibold">Contact Enquiries</h2>
              </div>

              {loading ? (
                <div className="p-8 text-center text-muted-foreground">
                  <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                  Loading enquiries...
                </div>
              ) : filteredEnquiries.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  {searchTerm || statusFilter !== "all"
                    ? "No enquiries match your filters"
                    : "No enquiries yet"}
                </div>
              ) : (
                <div className="divide-y divide-border-gray max-h-[600px] overflow-y-auto">
                  {filteredEnquiries.map((enquiry) => (
                    <div
                      key={enquiry.id}
                      className={`p-4 hover:bg-cream/50 cursor-pointer transition-colors ${
                        selectedEnquiry?.id === enquiry.id ? "bg-cream" : ""
                      }`}
                      onClick={() => setSelectedEnquiry(enquiry)}
                      data-testid={`enquiry-row-${enquiry.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-charcoal truncate">
                              {enquiry.name}
                            </span>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-sm ${getStatusColor(
                                enquiry.status
                              )}`}
                            >
                              {enquiry.status}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground truncate">
                            {enquiry.email}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {enquiry.message}
                          </p>
                        </div>
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatDate(enquiry.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white border border-border-gray rounded-sm sticky top-24">
              <div className="bg-gold text-navy px-4 py-3">
                <h2 className="font-heading font-semibold">Enquiry Details</h2>
              </div>

              {selectedEnquiry ? (
                <div className="p-4">
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wide">
                        Name
                      </label>
                      <p className="font-semibold text-charcoal">{selectedEnquiry.name}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wide">
                        Email
                      </label>
                      <a
                        href={`mailto:${selectedEnquiry.email}`}
                        className="block text-charcoal hover:text-gold transition-colors break-all"
                      >
                        {selectedEnquiry.email}
                      </a>
                    </div>
                    {selectedEnquiry.phone && (
                      <div>
                        <label className="text-xs text-muted-foreground uppercase tracking-wide">
                          Phone
                        </label>
                        <a
                          href={`tel:${selectedEnquiry.phone}`}
                          className="block text-charcoal hover:text-gold transition-colors"
                        >
                          {selectedEnquiry.phone}
                        </a>
                      </div>
                    )}
                    {selectedEnquiry.company && (
                      <div>
                        <label className="text-xs text-muted-foreground uppercase tracking-wide">
                          Company
                        </label>
                        <p className="text-charcoal">{selectedEnquiry.company}</p>
                      </div>
                    )}
                    {selectedEnquiry.service_interest && (
                      <div>
                        <label className="text-xs text-muted-foreground uppercase tracking-wide">
                          Service Interest
                        </label>
                        <p className="text-charcoal">{selectedEnquiry.service_interest}</p>
                      </div>
                    )}
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wide">
                        Message
                      </label>
                      <p className="text-charcoal whitespace-pre-wrap">
                        {selectedEnquiry.message}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wide">
                        Received
                      </label>
                      <p className="text-charcoal">{formatDate(selectedEnquiry.created_at)}</p>
                    </div>

                    <div className="pt-4 border-t border-border-gray">
                      <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
                        Update Status
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { v: "new", label: "New", cls: "bg-gold text-navy" },
                          {
                            v: "contacted",
                            label: "Contacted",
                            cls: "bg-navy text-cream",
                          },
                          {
                            v: "completed",
                            label: "Completed",
                            cls: "bg-forest-green text-white",
                          },
                        ].map((s) => (
                          <button
                            key={s.v}
                            onClick={() => updateStatus(selectedEnquiry.id, s.v)}
                            className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${
                              selectedEnquiry.status === s.v
                                ? s.cls
                                : "bg-warm-gray text-charcoal hover:opacity-80"
                            }`}
                            data-testid={`status-${s.v}-btn`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="pt-4">
                      <button
                        onClick={() => deleteEnquiry(selectedEnquiry.id)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-sm transition-colors"
                        data-testid="delete-enquiry-btn"
                      >
                        <Trash2 size={16} />
                        Delete Enquiry
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  <Eye size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Select an enquiry to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#FDFBF7",
            border: "1px solid #D4D4D0",
            color: "#1A1A1A",
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />
    </div>
  );
}
